"use client";

import Image from "next/image";
import "@/styles/components/shipping-restrictions.scss";
import Navbar from "@/components/common/Navbar";
import Footer from "@/components/common/Footer";
import { useState } from "react";
import SearchBar from "../../components/common/SearchBar";

const restrictedItems = {
  A: [
    "Agricultural products, including certain seeds, live or dead plants, unfinished or untreated wood & soil",
    "Ammunition, magazines & bayonets",
    "Any dual-use or commercial article controlled under the Commerce Control List (CCL) as defined under the Export Administration Regulations, where the control status requires a Bureau of Industry and Security (BIS) approved export license",
    "Any food, supplement, drug or cosmetic that has been issued a Consumer Safety Advisory Warning",
    "Any unidentifiable material, substance or chemical",
  ],
  B: [
    "BB/pellet/airsoft/paintball guns, parts and projectiles",
    "Body armor, helmets or personal protection articles with kevlar or ballistic ratings",
    "Brazilian rosewood",
  ],
  C: [
    "Contraband or illegal substances",
    "Coral",
    "Cosmetics",
    "Counterfeit products",
  ],
  D: [
    "Damaged batteries",
    "Defense articles controlled under the US Munitions List as defined under the International Traffic in Arms Regulations",
    "Dietary supplements",
  ],
  E: ["Explosives, fireworks, gun powder, flares or matches"],
  F: [
    "Fire extinguishers",
    "Firearms, guns & accessories, including replicas",
    "Food",
  ],
  G: [
    "Gambling devices & accessories",
    "Gas masks & gas mask filters",
    "Gasoline, diesel or other fuels",
    "Government, police or military uniforms, IDs and Badges (real or replica)",
  ],
  H: [
    "Handcuffs of any material, including plastic zip tie restraints & straitjackets",
    "Human remains",
  ],
  I: [
    "Infectious substances",
    "Items containing animal products controlled under the Endangered Species Act, Marine Mammal Protection Act, or requiring a permit under CITES",
  ],
  L: [
    "Lab reagents, biologics, cultures, medical specimens",
    "Law enforcement striking weapons, including saps, batons & billy clubs",
    "Lighters containing fuel",
    "Live or dead animals or insects",
    "Lock picking devices",
    "Lottery tickets",
  ],
  M: [
    "Meals Ready to Eat (MRE)",
    "Medical devices not FDA-approved",
    "Military and law enforcement equipment",
    "Military and/or dual-use flight helmets and flight jumpsuits",
    "Military training equipment",
    "Military/tactical/police shields",
  ],
  N: ["Non-prescription medications"],
  O: ["Oxidizing agents"],
  P: [
    "Perishable products of any kind",
    "Pesticides, herbicides, fungicides",
    "Poisonous substances",
    "Prescription medical devices",
    "Prescription medications, including dental & veterinary",
    "Prohormones, Human Growth Hormones, stem cell treatments, steroids or synthetic versions",
  ],
  R: [
    "Radioactive elements or products",
    "Rifle scopes, laser pointing & aiming devices for firearms",
    "Rough diamonds",
  ],
  S: [
    "Self-balancing boards (hoverboards)",
    "Self-propelled vehicles",
    "Skin or leather of snakes, alligators, crocodiles, stingrays, and other reptiles or amphibians",
    "Skin, fur or leather of nearly all animals, but especially wolves, bears, elephants, rhinos and certain deer and foxes",
    "Stun guns & tasers",
    "Sturgeon or Beluga caviar",
  ],
  T: [
    "Tear gas, mace & pepper spray",
    "The following items that are not labeled in English, or without FDA-approved labeling requirements:",
    "Thermal imaging, InfraRed or other night vision devices",
    "Tobacco products (including E-cigarettes and vaping products)",
    "Toxic substances, including inhalation hazards",
  ],
};

const leftLetters = ["A", "B", "C", "D", "E", "F", "G", "H", "I"];
const rightLetters = ["L", "M", "N", "O", "P", "R", "S", "T"];

// Wraps matched substring in <mark> for highlighting
function HighlightedText({ text, query }) {
  if (!query.trim()) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="search_highlight">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export default function ShippingRestrictions() {
  const [query, setQuery] = useState("");

  const allItems = Object.entries(restrictedItems).flatMap(([letter, items]) =>
    items.map((item) => ({ letter, item }))
  );

  const isSearching = query.trim().length > 0;

  const filtered = isSearching
    ? allItems.filter(({ item }) =>
        item.toLowerCase().includes(query.toLowerCase())
      )
    : [];

  return (
    <>
      <div className="section">
        <Navbar />
      </div>

      <div className="shipping_page">
        {/* ── LIGHT HEADER BANNER ── */}
        <section className="shipping_header">
          <h1>
            What Can't Be Shipped <span>Internationally</span>
          </h1>
          <div className="header_image">
            <Image
              src="/images/icons/box.png"
              alt="box with question mark"
              width={220}
              height={220}
            />
          </div>
        </section>

        {/* ── DARK MAIN CONTENT ── */}
        <section className="shipping_content">
          <div className="content_wrapper">

            {/* TITLE + ICONS ROW */}
            <div className="intro_row">
              <div className="intro_title">
                <h2>
                  Can I Ship It
                  <br />
                  <span>Internationally?</span>
                </h2>
              </div>
              <div className="icons_row">
                <div className="icon_box">
                  <Image src="/images/icons/gun.png" alt="gun" width={36} height={36} />
                </div>
                <div className="icon_box">
                  <Image src="/images/icons/poison.png" alt="poison" width={36} height={36} />
                </div>
                <div className="icon_box">
                  <Image src="/images/icons/fruits.png" alt="food" width={36} height={36} />
                </div>
                <div className="icon_box">
                  <Image src="/images/icons/medicine.png" alt="medicine" width={36} height={36} />
                </div>
                <div className="icon_box">
                  <Image src="/images/icons/plantpot.png" alt="plant" width={36} height={36} />
                </div>
              </div>
            </div>

            {/* DESCRIPTION */}
            <p className="intro_desc">
              There are some items Baskitt cannot legally ship due to customs
              laws and delivery company or airline restrictions. Banned items
              can vary depending on the destination country (
              <a href="#">check your country&apos;s restrictions here</a>
              ), but in general, you cannot ship these items from the USA:
            </p>

            {/* SEARCH BAR */}
            <div className="search-container">
              <SearchBar
                value={query}
                onChange={(val) => setQuery(val)}
                onSearch={(val) => setQuery(val)}
              />
            </div>

            {/* ── SEARCH RESULTS ── */}
            {isSearching && (
              <div className="search_results">
                {filtered.length === 0 ? (
                  <p className="no_results">
                    No restricted items found matching &ldquo;{query}&rdquo;
                  </p>
                ) : (
                  <>
                    <p className="results_count">
                      {filtered.length} item{filtered.length !== 1 ? "s" : ""} found
                    </p>
                    <ul>
                      {filtered.map(({ letter, item }, i) => (
                        <li key={i}>
                          <span className="result_letter">{letter}</span>
                          <HighlightedText text={item} query={query} />
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            {/* ── A–Z LIST GRID (hidden while searching) ── */}
            {!isSearching && (
              <div className="list_grid">
                <div className="list_column">
                  {leftLetters.map((letter) => (
                    <div className="letter_group" key={letter}>
                      <div className="letter_heading">
                        <span>{letter}</span>
                        <div className="letter_line" />
                      </div>
                      <ul>
                        {restrictedItems[letter].map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="list_column">
                  {rightLetters.map((letter) => (
                    <div className="letter_group" key={letter}>
                      <div className="letter_heading">
                        <span>{letter}</span>
                        <div className="letter_line" />
                      </div>
                      <ul>
                        {restrictedItems[letter].map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}